import { requestConfirmation } from "../lib/confirmation";
import { Controller } from "@hotwired/stimulus";
import { i18n } from "../lib/i18n";

/**
 * Broadcast Compose Controller
 *
 * Enhances the broadcast compose form with:
 * - Live recipient count that updates when scope changes
 * - Confirmation dialog before sending with recipient count
 *
 * The confirmation intercepts the form's submit event. The send button
 * (which may live outside this controller's DOM, e.g., in the drawer footer)
 * submits the form by its ID. This controller catches that submit event
 * and shows a confirmation dialog before allowing it to proceed.
 */
export default class extends Controller {
  static targets = ["recipientCount", "form"];
  static values = {
    recipientCountUrl: String,
    count: { type: Number, default: 0 },
  };

  declare readonly recipientCountTarget: HTMLElement;
  declare readonly hasRecipientCountTarget: boolean;
  declare readonly formTarget: HTMLFormElement;
  declare readonly hasFormTarget: boolean;

  declare recipientCountUrlValue: string;
  declare countValue: number;

  private confirmed: boolean = false;

  connect() {
    this.fetchRecipientCount();
  }

  scopeChanged() {
    this.fetchRecipientCount();
  }

  async confirmSubmit(event: Event) {
    // If already confirmed (re-submit after confirmation), allow through
    if (this.confirmed) {
      this.confirmed = false;
      return;
    }

    event.preventDefault();

    const message =
      this.countValue > 0
        ? i18n.t("broadcast_compose.confirm_send", {
            count: this.countValue,
          })
        : i18n.t("broadcast_compose.confirm_send_generic");

    if (await requestConfirmation(message)) {
      this.confirmed = true;
      if (this.hasFormTarget) {
        this.formTarget.requestSubmit(((event as SubmitEvent).submitter || undefined) as HTMLButtonElement | undefined);
      }
    }
  }

  private async fetchRecipientCount() {
    const scope = this.selectedScope;
    if (!scope) return;

    if (this.hasRecipientCountTarget) {
      this.recipientCountTarget.textContent = i18n.t(
        "broadcast_compose.loading"
      );
    }

    try {
      const response = await fetch(this.recipientCountUrlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "X-CSRF-Token": this.csrfToken,
        },
        body: JSON.stringify({ scope }),
      });

      if (!response.ok) {
        this.updateCountDisplay(0);
        return;
      }

      const data = await response.json();
      this.countValue = data.count;
      this.updateCountDisplay(data.count);
    } catch {
      this.updateCountDisplay(0);
    }
  }

  private updateCountDisplay(count: number) {
    if (!this.hasRecipientCountTarget) return;

    if (count > 0) {
      this.recipientCountTarget.textContent = `(${i18n.t("broadcast_compose.recipients", { count })})`;
    } else {
      this.recipientCountTarget.textContent = "";
    }
  }

  private get selectedScope(): string | null {
    const checked = this.element.querySelector<HTMLInputElement>(
      'input[name="scope"]:checked'
    );
    return checked ? checked.value : null;
  }

  private get csrfToken(): string {
    const meta = document.querySelector('meta[name="csrf-token"]');
    return meta ? meta.getAttribute("content") || "" : "";
  }
}
